import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, PasswordInput, PinInput, Stack, Text, TextInput } from "@mantine/core";
import { CenteredCard } from "@/components/CenteredCard";
import { authFetch } from "@/features/oauth/api";
import { authSearchSchema } from "@/features/auth/redirect";
import { queryClient } from "@/lib/queryClient";
function ForgotPassword() {
  const { next, email: initialEmail } = Route.useSearch();
  const [email, setEmail] = useState(initialEmail ?? "");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [sent, setSent] = useState(false);
  const send = useMutation({
    mutationFn: () => authFetch("/email-otp/request-password-reset", { email }),
    onSuccess: () => setSent(true),
  });
  const reset = useMutation({
    mutationFn: () => authFetch("/email-otp/reset-password", { email, otp, password }),
    onSuccess: () => queryClient.clear(),
  });
  if (reset.isSuccess)
    return (
      <CenteredCard
        title="Password updated"
        description="Your previous sessions have been signed out. Sign in with your new password to continue."
      >
        <Button
          component="a"
          href={`/auth/sign-in?${new URLSearchParams({ ...(next ? { next } : {}), email })}`}
        >
          Sign in
        </Button>
      </CenteredCard>
    );
  return (
    <CenteredCard
      title="Reset your password"
      description="Your invitation or authorization request will be kept while you reset your password."
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (sent) reset.mutate();
          else send.mutate();
        }}
      >
        <Stack>
          <TextInput
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            disabled={sent}
            onChange={(event) => setEmail(event.currentTarget.value)}
          />
          {sent ? (
            <>
              <Text size="sm">If this account exists, we sent a 6-digit reset code.</Text>
              <PinInput
                aria-label="Reset code"
                length={6}
                type="number"
                oneTimeCode
                value={otp}
                onChange={setOtp}
              />
              <PasswordInput
                label="New password"
                visibilityToggleButtonProps={{ "aria-label": "Toggle new password visibility" }}
                autoComplete="new-password"
                minLength={8}
                required
                value={password}
                onChange={(event) => setPassword(event.currentTarget.value)}
              />
              <PasswordInput
                label="Confirm password"
                visibilityToggleButtonProps={{ "aria-label": "Toggle confirmation password visibility" }}
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(event) => setConfirm(event.currentTarget.value)}
              />
            </>
          ) : null}
          {send.error || reset.error ? (
            <Alert color="red">{send.error?.message ?? reset.error?.message}</Alert>
          ) : null}
          <Button
            type="submit"
            loading={send.isPending || reset.isPending}
            disabled={sent && (otp.length !== 6 || password.length < 8 || password !== confirm)}
          >
            {sent ? "Reset password" : "Send reset code"}
          </Button>
          {sent ? (
            <Button variant="default" loading={send.isPending} onClick={() => send.mutate()}>
              Resend code
            </Button>
          ) : null}
        </Stack>
      </form>
    </CenteredCard>
  );
}
export const Route = createFileRoute("/auth/forgot-password")({
  validateSearch: authSearchSchema,
  component: ForgotPassword,
});
